import { Controller, useForm, useFormContext } from 'react-hook-form';
import { DatePicker, Input, UISelect } from '../common/forms';
import React, { useState } from 'react';
import { Translate, useTranslate } from '~/i18n';
import { successMessage } from '~/utils/toasts';

import Axios from '~/utils/axios';
import { DateTime } from 'luxon';
import { File } from '~/components/common/forms';
import { RequestFooter } from './request-footer';
import { sentryCaptureException } from '~/errors/sentry';
import { useRouter } from 'next/router';
import { renderErrorsToToaster } from './render-error-to-toaster';
import { toBase64 } from '~/utils/to-base-64';
import { isBase64 } from '~/utils/isBase64';
import { isBase64Encoded } from '~/utils/isBase64Encoded';
import { AddButton } from '../common/button/add';
import { TrashIconOutline } from '~/svgs';

export const AccommodationDetails = ({ hotelsList, citiesList }: any) => {
   const { translate, lang } = useTranslate();
   const router = useRouter();
   const [loading, setLoading] = useState(false);
   const { id } = router.query;

   const { getValues } = useFormContext();
   const accomodationValues = getValues('accomodations');
   const [accommodations, setAccommodations] = useState(
      accomodationValues ? accomodationValues.length - 1 : 0
   );
   const {
      control,
      formState: { errors },
      handleSubmit,
      setValue,
      setError,
      register,
      reset,
      watch,
      getValues: getFormValues,
   } = useForm<any>({
      defaultValues: {
         accommodations: accomodationValues ? [...accomodationValues] : undefined,
      } as any,
   });

   const getFileExtension = (base64String: string) => {
      const supportedFormats: { [key: string]: string } = {
         'data:image/png;base64': 'png',
         'data:image/jpeg;base64': 'jpeg',
         'data:image/gif;base64': 'gif',
         'data:image/jpg;base64': 'jpg',
         'data:application/pdf;base64': 'pdf',
      };

      const prefix = Object.keys(supportedFormats).find(format => base64String?.startsWith(format));

      if (prefix) {
         return supportedFormats[prefix];
      } else {
         return;
      }
   };

   const validateHotelPrice = (value: any) => {
      if (value <= 0) {
         return translate({ id: 'validation:required' });
      }

      if (value?.startsWith?.('0')) {
         return translate({ id: 'validation:not starting with zero' });
      }

      const validNumberRegex = /^[0-9٠-٩]+(\.[0-9٠-٩]{1,2})?$/;

      if (!validNumberRegex.test(value)) {
         return translate({ id: 'validation:invalid format hotel price' });
      }

      return true;
   };

   const submitForm = async (values: any) => {
      const accommodationsObject = values?.accommodations;

      if (!accommodationsObject || typeof accommodationsObject !== 'object') {
         console.error('Invalid accommodations object');
         return;
      }

      const accommodationsArray = Object.values(accommodationsObject);
      if (!Array.isArray(accommodationsArray)) {
         console.error('Invalid accommodations array');
         return;
      }

      // Remove null objects from accommodationsArray
      const filteredAccommodationsArray = accommodationsArray.filter(
         accommodation => accommodation !== null && typeof accommodation === 'object'
      );

      const attachments = await Promise.all(
         filteredAccommodationsArray.map(async (accommodation: any) => {
            const file = accommodation?.attachment?.[0];
            if (!file) {
               setError(
                  `accommodations[${filteredAccommodationsArray.indexOf(
                     accommodation
                  )}].attachment`,
                  {
                     message: translate({ id: 'validation:required' }),
                  }
               );
               return null; // Returning null for the missing attachment
            }
            const isBase64EncodedFile = isBase64Encoded(accommodation?.attachment);
            const attachment =
               isBase64(accommodation?.attachment) || isBase64EncodedFile
                  ? accommodation?.attachment
                  : (await toBase64(file)) || '';
            return {
               attachment,
               isBase64Encoded: isBase64EncodedFile,
            };
         })
      );

      const newValues = {
         current_step: 'main_information_accommodation',
         accomodations: attachments.map((attachmentInfo: any, index: number) => {
            const { attachment, isBase64Encoded } = attachmentInfo || {};
            // Exclude fileType if isBase64Encoded is true
            const additionalProps = !isBase64Encoded
               ? {}
               : { fileType: getFileExtension(attachment) };
            const accommodationItem: any = filteredAccommodationsArray[index];
            return {
               ...(accommodationItem || {}),
               attachment,
               ...additionalProps,
               hotelPrice: parseFloat(accommodationItem?.hotelPrice || 0)?.toFixed(2),
            };
         }),
      };

      setLoading(true);

      try {
         const { data } = await Axios.patch(`/dhamen/v1/visa-request/${id}`, newValues);
         if (data?.status.toLowerCase() === 'success') {
            reset({ ...getValues(), ...values });
            successMessage(translate({ id: 'visasManager:submit message' }));
            router.push(`/${lang}/umrah-requests/${id}/edit/additional-services`);
         } else {
            throw new Error(data?.error || data?.original?.error || 'Something went wrong');
         }
      } catch (error: any) {
         console.error(error);
         sentryCaptureException(error);
         renderErrorsToToaster(error?.response?.data);
      }

      setLoading(false);
   };

   const Accommodation = ({ index }: { index: number }) => {
      const selectedCity = watch(`accommodations[${index}].city`);
      const filteredHotelsList = hotelsList.filter(
         (hotel: any) => hotel.splCityCode === selectedCity
      );

      const handleDelete = () => {
         setAccommodations(prevAccommodations => Math.max(prevAccommodations - 1, 0));
         setValue(`accommodations[${index}]`, null);
      };

      return (
         <div className="p-5 mb-8 border rounded-md bg-grey-600 border-grey-500 last:mb-0">
            <div className="mb-5">
               <Controller
                  name={`accommodations[${index}].city`}
                  control={control}
                  rules={{ required: translate({ id: 'validation:required' }) }}
                  render={({ field: { onChange, value } }) => (
                     <UISelect
                        id="city"
                        label={<Translate id="visasManager:city" />}
                        placeholder={translate({ id: 'visasManager:enter city name' })}
                        value={citiesList.find((opt: any) => opt.value === value)}
                        options={citiesList}
                        error={(errors as any)?.accommodations?.[index]?.city?.message}
                        onChange={({ value }: any) => {
                           onChange(value);
                           setValue('hotelCode', null);
                        }}
                        isRequired
                     />
                  )}
               />
            </div>
            <div className="mb-5">
               <Controller
                  name={`accommodations[${index}].hotelCode`}
                  control={control}
                  rules={{ required: translate({ id: 'validation:required' }) }}
                  render={({ field: { onChange, value } }) => (
                     <UISelect
                        label={<Translate id="visasManager:hotel code" />}
                        placeholder={translate({ id: 'visasManager:code' })}
                        id="hotelCode"
                        value={filteredHotelsList.find((opt: any) => opt.value === value)}
                        options={filteredHotelsList}
                        error={(errors as any)?.accommodations?.[index]?.hotelCode?.message}
                        onChange={({ value }: any) => onChange(value)}
                        isRequired
                        isDisabled={Boolean(!selectedCity?.length)}
                     />
                  )}
               />
            </div>
            <div className="flex flex-col items-start justify-between gap-5 mb-5 lg:flex-row">
               <Controller
                  control={control}
                  name={`accommodations[${index}].checkInDate`}
                  rules={{ required: translate({ id: 'validation:required' }) }}
                  render={({ field: { onChange, value } }) => (
                     <DatePicker
                        placeholderText={translate({
                           id: 'common:date',
                        })}
                        id="checkInDate"
                        minDate={
                           DateTime.fromFormat(
                              getValues('dateArrivalFlight'),
                              'yyyy-MM-dd'
                           ).toJSDate() ?? new Date()
                        }
                        maxDate={
                           DateTime.fromFormat(getValues('flightDepartureDate'), 'yyyy-MM-dd')
                              .minus({ days: 1 })
                              .toJSDate() ?? null
                        }
                        label={translate({ id: 'visasManager:check in date' })}
                        value={value?.split?.('T')?.[0] || value}
                        error={(errors as any)?.accommodations?.[index]?.checkInDate?.message}
                        onChange={val => {
                           const d = DateTime.fromJSDate(val as Date);
                           onChange(d.toFormat('yyyy-MM-dd'));
                           setValue('checkOutDate', '');
                        }}
                        groupClassName="w-full lg:w-1/2"
                        isRequired
                     />
                  )}
               />
               <Controller
                  control={control}
                  name={`accommodations[${index}].checkOutDate`}
                  rules={{ required: translate({ id: 'validation:required' }) }}
                  render={({ field: { onChange, value } }) => {
                     const checkInDateValue = watch('checkInDate');
                     const minDate =
                        (checkInDateValue &&
                           DateTime.fromFormat(checkInDateValue, 'yyyy-MM-dd').toJSDate()) ||
                        DateTime.fromFormat(
                           getValues('dateArrivalFlight'),
                           'yyyy-MM-dd'
                        ).toJSDate() ||
                        new Date();

                     return (
                        <DatePicker
                           placeholderText={translate({
                              id: 'common:date',
                           })}
                           id="checkOutDate"
                           minDate={minDate}
                           maxDate={
                              getValues('flightDepartureDate') &&
                              DateTime.fromFormat(
                                 getValues('flightDepartureDate'),
                                 'yyyy-MM-dd'
                              ).toJSDate()
                           }
                           label={translate({ id: 'visasManager:check out date' })}
                           value={value?.split?.('T')?.[0] || value}
                           error={(errors as any)?.accommodations?.[index]?.checkOutDate?.message}
                           onChange={val => {
                              const d = DateTime.fromJSDate(val as Date);
                              onChange(d.toFormat('yyyy-MM-dd'));
                           }}
                           groupClassName="w-full lg:w-1/2"
                           isRequired
                        />
                     );
                  }}
               />
            </div>
            <div className="mb-5">
               <Input
                  id="hotelPrice"
                  label={translate({
                     id: 'visasManager:hotel price',
                  })}
                  placeholder={translate({
                     id: 'visasManager:enter hotel price',
                  })}
                  isRequired
                  error={(errors as any)?.accommodations?.[index]?.hotelPrice?.message}
                  {...register(`accommodations[${index}].hotelPrice`, {
                     validate: validateHotelPrice,
                  })}
               />
            </div>
            <div className="mb-5">
               <File
                  label={
                     <div className="flex items-center gap-1 font-semibold sm:text-base">
                        <div>
                           <Translate id={'visasManager:attachment'} />
                        </div>
                     </div>
                  }
                  {...register(`accommodations[${index}].attachment`)}
                  placeholder={translate({
                     id: 'validation:attachment placeholder',
                     values: {
                        type: 'kb',
                        amount: '100',
                     },
                  })}
                  defaultImage={
                     (getFormValues(`accommodations[${index}].attachment`) !== undefined &&
                        getFormValues(`accommodations[${index}].attachment`)) ||
                     accomodationValues?.[index]?.attachment ||
                     ''
                  }
                  groupClassName="!mb-8 col-span-2 flex flex-col justify-between"
                  error={(errors as any)?.accommodations?.[index]?.attachment?.message}
                  setError={(message: string) =>
                     setError(`accommodations[${index}].attachment`, { message })
                  }
                  size_limit={0.1}
                  min_size_limit={0.0033}
                  size_limit_type="KB"
                  is_required
                  accept="image/jpeg,image/gif,image/png,application/pdf,image/x-eps"
                  max_pdf_pages={1}
                  isEncodedBase64={Boolean(
                     isBase64Encoded(watch(`accommodations[${index}].attachment`))
                  )}
                  description={
                     <ul className="list-disc list-inside">
                        <li>
                           {translate({
                              id: 'validation:max pdf pages',
                              values: {
                                 max_pdf_pages: String(1),
                              },
                           })}
                        </li>
                        <li>
                           {translate({
                              id: 'validation:attachment placeholder',
                              values: {
                                 amount: '100',
                                 type: 'kb',
                              },
                           })}
                        </li>
                        <li>
                           {translate({
                              id: 'validation:min file size with type',
                              values: {
                                 amount: '3.3',
                                 type: 'kb',
                              },
                           })}
                        </li>
                     </ul>
                  }
               />
            </div>
            {accommodations > 0 && (
               <div className="mt-5">
                  <button
                     type="button"
                     onClick={handleDelete}
                     className="flex items-center gap-2 text-states-error hover:text-red-700 focus:outline-none">
                     <TrashIconOutline />
                     <Translate id="common:delete" />
                  </button>
               </div>
            )}
         </div>
      );
   };

   return (
      <>
         <div>
            <div className="mb-8 text-xl font-bold text-primary">
               <Translate id="visasManager:accommodation details" />
            </div>

            <div className="w-full pb-8">
               <form onSubmit={handleSubmit(submitForm)}>
                  <Accommodation index={0} />
                  {[...Array(accommodations)].map((_, i) => (
                     <Accommodation key={i + 1} index={i + 1} />
                  ))}
               </form>
            </div>
            <div className="my-5">
               <div className="my-5">
                  <AddButton
                     onClick={() => {
                        setAccommodations(prevAccommodations => prevAccommodations + 1);
                        setValue(`accomodations[${accommodations + 1}]`, {}); // Use updated value of accommodations
                     }}
                     title={<Translate id="visasManager:add another accommodation" />}
                  />
               </div>
            </div>
         </div>
         <RequestFooter
            fwdBtnHandler={handleSubmit(submitForm)}
            fwdBtnLabel={translate({ id: 'common:next' })}
            cancelBtnHandler={() =>
               router.push(`/${lang}/umrah-requests/${id}/edit/supervisor-and-arrival-details`)
            }
            loading={loading}
         />
      </>
   );
};
